/* eslint-disable no-secrets/no-secrets */
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, map, combineLatest, filter, take } from 'rxjs';

import { GridElement, GridDefault, PageProductsLoad, RelatedProductsCustomGridConfig } from '../models/custom-category.models';

import { getCookie } from '@core/application/use-cases/cookies/get-cookie.use-case';
import { Category, AttachmentCategory } from '@core/domain/models/category.models';
import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { MediasPublicConstants } from '@features/products/medias';
import { ProductPublicModels } from '@features/products/product';
import { ProductsArrayPublicModels } from '@features/products/productsArray';
import { isDefined } from '@frontend/common/utils';
import { Resolutions } from '@layout/domain/models/layout.models';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { CategoryFilterState, CustomCategories } from '@pages/category/public_api';
import { getCategory, getCategoryAttachments, getFilters, getGridElements, getProductsId } from '@pages/category/store/category-page.selectors';
import { getGridType } from '@pages/category/store/grid-type/grid-type.selectors';
import { AttachmentsEnums } from '@shared/header/services/attachments.enums';
import { GridType, GridTypes } from '@shared/models/grid-type.model';
import { ProductViewConfig, Position, ProductViewPlaceValue } from '@shared/product/models';
import { getRecommendationsByPlacement } from '@shared/product/related-products/application/use-cases/recommendations-by-placement.uc';
import { RecommendationsByPlacementParamsGroup } from '@shared/product/related-products/domain/models/recommendations-by-placement.model';
import {
    RelatedProductsRepositoryInterface,
} from '@shared/product/related-products/domain/related-products-repository.interface';
import { RELATED_PRODUCTS_REPOSITORY } from '@shared/product/related-products/ui/tokens/related-products.tokens';

@Injectable()
export class CustomCategoryGridService {
    public readonly getProductsId$: Observable<number[]> = this.store.select(getProductsId);

    public getGridType$: Observable<GridType> = this.store.select(getGridType);

    public getGridElements$: Observable<GridElement[]> = this.store.select(getGridElements);

    public categoryAttachments$: Observable<any> = this.store.select(getCategoryAttachments);

    public isMobile$: Observable<boolean> = this.store.select(getResolution)
        .pipe(map((resolutions: Resolutions) => resolutions === Resolutions.MOBILE));

    public getCategory$: Observable<Category> = this.store.select(getCategory);

    public getFilters$: Observable<CategoryFilterState> = this.store.select(getFilters);

    constructor(
        private readonly store: Store,
        @Inject(RELATED_PRODUCTS_REPOSITORY) private readonly relatedProductsRepository: RelatedProductsRepositoryInterface,
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly platformService: CorePlatformService
    ) { }

    public mapStateGridDefault(): Observable<GridDefault> {
        return combineLatest([
            this.getProductsId$.pipe(filter(value => isDefined(value))),
            this.getFilters$,
            this.getCategory$,
            this.getGridType$,
            this.getGridElements$.pipe(filter(value => value?.length > 0)),
        ]).pipe(
            take(1),
            map(([productsId, filters, category, gridType, gridElements]) => {
                return CustomCategoryGridService.buildGridDefaultState(
                    productsId,
                    filters,
                    category,
                    gridType,
                    gridElements
                );
            }),
        );
    }

    /**
     * Función encargada de recuperar un conjunto de identificadores de producto recomendados en función de la estrategia RFY de MECACO
     *
     * @param lastSection string
     * @param config RelatedProductsCustomGridConfig
     *
     * @returns number[]
     */
    public fetchRecommendationsByRfy$(
        lastSection: string,
        config: RelatedProductsCustomGridConfig,
    ): Observable<number[]> {
        if (this.platformService.isBrowser()) {
            const sessionId = getCookie(this.document.cookie, 'ITXSESSIONID');
            const googleAnalyticsCookie = getCookie(this.document.cookie, '_ga');

            const getSectionName: Record<string, number> = {
                WOMEN: 1,
                MEN: 2,
            };

            const paramsGroup: RecommendationsByPlacementParamsGroup = {
                placeName: ProductViewPlaceValue.PRODUCT_GRID,
                type: 'recommended_for_you',
                sessionId,
                section: getSectionName[lastSection],
                campaign: config.campaign,
                limit: undefined,
                baseProducts: [],
                googleAnalyticsCookie,
            };

            return getRecommendationsByPlacement(
                this.relatedProductsRepository,
                this.store,
                paramsGroup,
            ).pipe(
                // eslint-disable-next-line camelcase
                map(recommendations => recommendations.map(({ product_id }) => +product_id)),
            );
        }
    }

    public static buildGridDefaultState(productsId, filters, category, gridType, gridElements): GridDefault {
        const pageSize = CustomCategoryGridService.getPageSize(gridType);

        // obtenemos los IDS a incluir a través de attachments shopBySize
        const filterByAttachment = CustomCategoryGridService.getAttachmentByPath(category, AttachmentsEnums.SHOP_BY_SIZE);

        const productsBySize = filterByAttachment
            ? CustomCategoryGridService.filterProductIdsByArraySizes(filterByAttachment.name?.split(','), filters.filters.sizeFilter)
            : [];

        // obtenemos los IDS que estan dentro del template para exluir
        const hideSeparators = category?.attachments?.find(
            (attachment) => attachment.name === AttachmentsEnums.HIDE_SEPARATORS
        );

        const excludedGridElements: number[] = gridElements.reduce((els, el) => {
            const hideSeparator = hideSeparators && el.template?.includes('SEPARATOR_');
            if (hideSeparator || el.template?.includes('SEC')) {
                const val = els.concat(el.ccIds);
                return val;
            }
            return els;
        }, []);

        // filtramos los ids totales en base a las necesidades de negocio
        const idsFiltered: number[] = productsId.reduce((ids, id) => {
            const isSizeMatch = !filterByAttachment || productsBySize.includes(id);
            if (isSizeMatch && !excludedGridElements.includes(id)) { ids.push(id); }
            return ids;
        }, []);

        const initPage = CustomCategoryGridService.sliceIdsWithPageSize(gridType, idsFiltered, 0);

        return {
            totalPagesIds: Math.ceil(idsFiltered.length / pageSize),
            idsFiltered,
            pageProductIds: initPage.ids,
            currentPage: initPage.page,
        };
    }

    public static mapStateGridCustomCategory(
        data: Array<{ categoryGrids: ProductsArrayPublicModels.PublicCategoryPlatformStudio }>,
        gridType: GridTypes
    ): GridDefault {
        const categoryProductsFlat = data.reduce((accumulatedItems, item) => {
            const { productIds } = item.categoryGrids;
            const pageSizeDefault = CustomCategoryGridService.getPageSize(gridType);
            const initPageDefault = { ids: [], page: 0 };
            const mapDefault = {
                totalPagesIds: Math.ceil(productIds.length / pageSizeDefault),
                idsFiltered: productIds,
                pageProductIds: productIds,
                currentPage: initPageDefault,
            };

            const idsFiltered = accumulatedItems?.idsFiltered
                ? accumulatedItems.idsFiltered.concat(mapDefault.idsFiltered) : mapDefault.idsFiltered;

            return {
                totalPagesIds: Math.ceil(idsFiltered.length / pageSizeDefault),
                idsFiltered,
                pageProductIds: initPageDefault.ids,
                currentPage: 0,
            };
        }, {
            currentPage: undefined, idsFiltered: undefined, pageProductIds: undefined, totalPagesIds: undefined,
        });

        return categoryProductsFlat;
    }

    public static getCustomCategory(category: Category): string {
        return category?.attachments?.find((attachment) => Object.values(CustomCategories).includes(attachment?.name))?.name;
    }

    private static filterProductIdsByArraySizes(
        arraySizes: string[],
        sizeFilter: any // ProductFilterOrderItem[]
    ): number[] {
        const skipIfSizeNotIncluded = (sizes, size) => arraySizes.includes(size?.name) ? sizes.concat(size) : sizes;
        const sizesIncluded = sizeFilter.reduce(skipIfSizeNotIncluded, []);
        if (sizesIncluded.length === 1) {
            return sizesIncluded[0].productIds;
        }
        return sizesIncluded.reduce((acc, curr) => ({ productIds: acc.productIds.concat(curr.productIds) })).productIds;
    }

    public static sliceIdsWithPageSize(
        gridType: GridType,
        allIds: number[],
        currentPage: number,
        pageProductIds = 0
    ): { ids: number[]; page: number } {
        // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
        let page;
        // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
        let startIndex;
        // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
        let endIndex;
        const pageSize = CustomCategoryGridService.getPageSize(gridType);

        if (pageProductIds > 0) {
            startIndex = pageProductIds;
            endIndex = pageProductIds + pageSize;
            const totalPages = Math.ceil(allIds.length / pageSize);
            const remainingPages = Math.ceil((allIds.length - endIndex) / pageSize);
            page = totalPages - remainingPages;
        } else {
            startIndex = currentPage * pageSize;
            endIndex = (currentPage + 1) * pageSize;
            page = currentPage + 1;
        }

        return { ids: allIds.slice(startIndex, endIndex), page };
    }

    // Se puede optimizar la carga en vista Mobile (solo son skeletors no tiene mejora de performance)
    public static getPageSize(gridType: GridTypes): number {
        switch (gridType) {
            case GridTypes.GRID_16:
                return PageProductsLoad.GRID_16;
            case GridTypes.GRID_4:
                return PageProductsLoad.GRID_4;
            default:
                return PageProductsLoad.ZARA_STUDIO;
        }
    }

    public static mapTemplateConfig(
        catalogStyles,
        place,
        config: RelatedProductsCustomGridConfig,
        location = MediasPublicConstants.PublicMediaLocationType.category_page,
        clickable = true
    ): ProductViewConfig {
        return {
            imageType: config?.productsImage,
            set: config?.productsSet,
            clickable,
            location,
            showDescription: false,
            showTags: true,
            showName: true,
            showPrice: true,
            showQuickly: true,
            showColors: true,
            textPosition: Position.bottom,
            showDiscount: true,
            showMadeInExclusive: true,
            styles: {
                backgroundColor: catalogStyles?.backgroundColor,
                fontColor: catalogStyles?.fontColor,
                offerPrice: catalogStyles?.offerPrice,
                colorSelector: catalogStyles?.colorSelector,
                cardColor: catalogStyles?.backgroundColor,
            },
            place,
            media: {
                ratio: '3-4',
                widthVW: '(max-width: 800) 50vw, 18vw',
                showWaterMark: false,
            },
        };
    }

    public static checkAttachmentName(category: Category, attachmentKey: string): boolean {
        return category?.attachments?.find((attachment: any) => attachment.name === attachmentKey) ?? false;
    }

    public static getAttachmentByPath(category: Category, attachmentKey: string): AttachmentCategory {
        return category?.attachments?.find((attachment: any) => attachment.path === attachmentKey);
    }

    public static applyFiltersToProductList(
        products: ProductPublicModels.PublicProduct[],
        categoryId: string,
        config: RelatedProductsCustomGridConfig,
    ): ProductPublicModels.PublicProduct[] {
        return products
            .filter(product => !CustomCategoryGridService.filtersForCustomCategories(product))
            .filter(product => CustomCategoryGridService.filtersProductByConfig(product, categoryId, config));
    }

    /**
     * Devolverá true en caso de que el producto tenga la visibilidad 'BACK_SOON', 'COMING_SOON', 'DISABLED' o 'HIDDEN' en todas las tallas
     * o cuando más del 80% de las tallas tengan una visibilidad de "COMING_SOON" o "BACK_SOON"
     *
     * @param product
     * @returns true si debe filtrarse, false en caso contrario
     */
    protected static filtersForCustomCategories(product: ProductPublicModels.PublicProduct): boolean {
        const filteredVisibility = ['BACK_SOON', 'COMING_SOON', 'DISABLED', 'HIDDEN'].includes(product.visibilityValue);
        const sizesQuantity = product.status?.selectedColor.sizes.length;
        const filteredVisibilitySizes = product.status?.selectedColor.sizes.reduce((count, size) => {
            if (['BACK_SOON', 'COMING_SOON'].includes(size.visibilityValue)) {
                return count + 1;
            }
            return count;
        }, 0);
        return filteredVisibility || filteredVisibilitySizes / sizesQuantity > 0.8;
    }

    /**
     * Función encargada de filtrar un producto en función de configuración CMS
     *
     * - Si el producto no está excluido por subFamily y la config showPromoProducts es true
     * - Si el producto no está excluido por subFamily, la config showPromoProducts es false y el producto no está en promoción
     *
     * @param product ProductPublicModels.PublicProduct
     * @param categoryId string
     * @param config RelatedProductsCustomGridConfig,
     *
     * @returns boolean
     */
    protected static filtersProductByConfig(
        product: ProductPublicModels.PublicProduct,
        categoryId: string,
        config: RelatedProductsCustomGridConfig,
    ): boolean {
        const isExcludedSubFamily = config.categoriesConfig[categoryId]?.excluded.subFamilyIds.includes(product.subFamily);

        return !isExcludedSubFamily && (config.showPromoProducts || !product.promotions.length);
    }
}
